import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowLeft,
  ShieldCheck,
  PlayCircle,
  Clock,
  Lock,
  HeartHandshake,
  CheckCircle2,
  MessageCircle,
  Sparkles,
  ChevronDown,
  CreditCard,
  Landmark,
} from 'lucide-react';
import { courseInfo, courseModules, courseFAQ, profile } from '../mock';

const buildWhatsAppLink = (currency) => {
  const price = currency === 'USD' ? `USD ${courseInfo.priceUSD}` : `$${courseInfo.priceUYU} UYU`;
  const method = currency === 'USD' ? 'PayPal (tarjeta internacional)' : 'transferencia bancaria local';
  const msg =
    `Hola Karina! Quiero inscribirme en el curso "${courseInfo.title}".%0A%0A` +
    `Forma de pago elegida: ${method}.%0A` +
    `Precio: ${price}.%0A%0A` +
    `Espero tu mensaje con los pasos a seguir. Gracias!`;
  return `${profile.whatsapp}?text=${msg}`;
};

const FAQItem = ({ q, a, idx }) => {
  const [open, setOpen] = useState(idx === 0);
  return (
    <div
      className="border-b border-[#DBCEB4]/70 last:border-b-0"
      data-testid={`curso-faq-item-${idx}`}
    >
      <button
        onClick={() => setOpen((v) => !v)}
        className="w-full text-left py-5 flex items-center justify-between gap-6 group"
        aria-expanded={open}
        data-testid={`curso-faq-toggle-${idx}`}
      >
        <span className="font-serif text-[19px] lg:text-[21px] text-[#2E2A23] group-hover:text-[#B8806E] transition-colors">
          {q}
        </span>
        <ChevronDown
          size={20}
          strokeWidth={1.6}
          className={`flex-none text-[#8A7E6E] transition-transform duration-300 ${
            open ? 'rotate-180 text-[#B8806E]' : ''
          }`}
        />
      </button>
      <div
        className={`overflow-hidden transition-[max-height,opacity] duration-500 ${
          open ? 'max-h-[280px] opacity-100' : 'max-h-0 opacity-0'
        }`}
      >
        <p className="pb-6 pr-8 text-[15px] leading-[1.8] text-[#2E2A23]/80">{a}</p>
      </div>
    </div>
  );
};

const CursoLanding = () => {
  const totalMinutes = useMemo(() => courseModules.length * 15, []);

  return (
    <div className="min-h-screen bg-[#F4EDE2] text-[#2E2A23]">
      {/* sticky header */}
      <header className="sticky top-0 z-30 bg-[#F4EDE2]/90 backdrop-blur-md border-b border-[#DBCEB4]">
        <div className="max-w-[1200px] mx-auto px-5 lg:px-8 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3" data-testid="curso-back-home">
            <span className="w-8 h-8 rounded-full bg-[#B8806E] text-[#FAF4E8] grid place-items-center font-serif text-base leading-none">
              k
            </span>
            <span className="hidden sm:flex flex-col leading-tight">
              <span className="font-serif text-[16px] text-[#2E2A23]">Karina Negrin</span>
              <span className="text-[10px] tracking-[0.22em] uppercase text-[#8A7E6E]">
                Curso virtual
              </span>
            </span>
          </Link>
          <div className="flex items-center gap-4">
            <a
              href="#inscripcion"
              className="hidden sm:inline-flex items-center gap-2 text-[12px] tracking-[0.16em] uppercase text-[#B8806E] hover:text-[#9A6555] transition-colors"
              data-testid="curso-header-cta"
            >
              Inscribirme
              <Sparkles size={14} strokeWidth={1.7} />
            </a>
            <Link
              to="/"
              className="inline-flex items-center gap-2 text-[12px] tracking-[0.16em] uppercase text-[#8A7E6E] hover:text-[#B8806E] transition-colors"
            >
              <ArrowLeft size={14} strokeWidth={1.7} />
              <span className="hidden sm:inline">Volver al sitio</span>
            </Link>
          </div>
        </div>
      </header>

      <main className="max-w-[1200px] mx-auto px-5 lg:px-8 py-14 lg:py-20">
        {/* HERO */}
        <section className="grid lg:grid-cols-[1.15fr,1fr] gap-12 lg:gap-16 items-center">
          <div>
            <div className="flex items-center gap-3 mb-5">
              <span className="inline-block w-9 h-px bg-[#B8806E]" />
              <span className="text-[11px] tracking-[0.28em] uppercase text-[#8A7E6E]">
                Curso virtual · acompañamiento online
              </span>
            </div>
            <h1
              className="font-serif text-[42px] sm:text-[52px] lg:text-[64px] leading-[1.02] tracking-[-0.02em] text-[#2E2A23]"
              data-testid="curso-hero-title"
            >
              Curso virtual de
              <span className="italic text-[#B8806E]"> extracción y lactancia</span>
            </h1>
            <p className="mt-7 text-[17px] leading-[1.75] text-[#2E2A23]/80 max-w-[600px] font-light">
              {courseInfo.description}
            </p>

            <div className="mt-8 flex flex-wrap gap-3">
              <span className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#FAF4E8] border border-[#DBCEB4] text-[12.5px] text-[#2E2A23]">
                <PlayCircle size={14} strokeWidth={1.7} className="text-[#B8806E]" />
                {courseModules.length} módulos en video
              </span>
              <span className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#FAF4E8] border border-[#DBCEB4] text-[12.5px] text-[#2E2A23]">
                <Clock size={14} strokeWidth={1.7} className="text-[#B8806E]" />
                ~{totalMinutes} min de contenido
              </span>
              <span className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#FAF4E8] border border-[#DBCEB4] text-[12.5px] text-[#2E2A23]">
                <ShieldCheck size={14} strokeWidth={1.7} className="text-[#8FA08A]" />
                Acceso por 6 meses
              </span>
            </div>

            <div className="mt-10 flex flex-wrap items-center gap-4">
              <a
                href="#inscripcion"
                className="btn-primary"
                data-testid="curso-hero-cta-primary"
              >
                <Sparkles size={16} strokeWidth={1.6} />
                Inscribirme al curso
              </a>
              <a
                href="#modulos"
                className="btn-ghost"
                data-testid="curso-hero-cta-secondary"
              >
                Ver los módulos
              </a>
            </div>

            <div className="mt-12 flex items-center gap-6 text-[#8A7E6E]">
              <div className="flex items-center gap-2 text-[12px]">
                <CheckCircle2 size={14} strokeWidth={1.8} className="text-[#8FA08A]" />
                Pago seguro · PayPal
              </div>
              <div className="flex items-center gap-2 text-[12px]">
                <CheckCircle2 size={14} strokeWidth={1.8} className="text-[#8FA08A]" />
                Devolución 7 días
              </div>
            </div>
          </div>

          {/* hero image */}
          <div className="relative">
            <div className="aspect-[4/5] rounded-[28px] overflow-hidden border-[6px] border-[#FAF4E8] shadow-[0_30px_60px_-30px_rgba(74,46,32,0.5)]">
              <img
                src={courseInfo.heroImage}
                alt="Curso de lactancia · Karina Negrin"
                className="w-full h-full object-cover photo-no-watermark"
              />
            </div>
            <div className="absolute -bottom-6 -left-4 lg:-left-8 bg-[#FAF4E8] border border-[#E8C9C0] rounded-2xl px-6 py-4 shadow-lg">
              <p className="font-script text-[#B8806E] text-[26px] leading-none">
                tu lactancia,
              </p>
              <p className="font-serif italic text-[#2E2A23] text-[18px] mt-1">
                a tu ritmo
              </p>
            </div>
            <div className="absolute -top-5 -right-4 lg:-right-8 w-16 h-16 rounded-full bg-[#FAF4E8] border border-[#DBCEB4] grid place-items-center shadow-md">
              <HeartHandshake size={26} strokeWidth={1.4} className="text-[#B8806E]" />
            </div>
          </div>
        </section>

        {/* AUDIENCE */}
        <section className="mt-24 lg:mt-32 grid lg:grid-cols-[0.9fr,1.1fr] gap-12 items-start">
          <div>
            <div className="text-[11px] tracking-[0.28em] uppercase text-[#8A7E6E] mb-4">
              Para vos
            </div>
            <h2 className="font-serif text-[34px] lg:text-[44px] leading-[1.06] tracking-[-0.015em]">
              Si te identificás con
              <span className="italic text-[#B8806E]"> alguna de estas situaciones</span>,
              este curso es para vos
            </h2>
          </div>
          <ul className="space-y-3" data-testid="curso-audience-list">
            {courseInfo.audience.map((it, i) => (
              <li
                key={i}
                className="flex items-start gap-4 px-6 py-4 rounded-2xl bg-[#FAF4E8] border border-[#DBCEB4]"
              >
                <span className="w-7 h-7 rounded-full bg-[#E8DDC8] grid place-items-center text-[#B8806E] font-serif flex-none">
                  {i + 1}
                </span>
                <span className="text-[15.5px] leading-[1.7] text-[#2E2A23]/90">
                  {it.text}
                </span>
              </li>
            ))}
          </ul>
        </section>

        {/* MODULES */}
        <section id="modulos" className="mt-24 lg:mt-32">
          <div className="flex items-end justify-between flex-wrap gap-4 mb-10">
            <div>
              <div className="text-[11px] tracking-[0.28em] uppercase text-[#8A7E6E] mb-3">
                Programa del curso
              </div>
              <h2 className="font-serif text-[36px] lg:text-[48px] leading-[1.05] tracking-[-0.015em] max-w-[680px]">
                <span className="italic text-[#B8806E]">8 módulos</span> para transitar
                tu lactancia con calma
              </h2>
            </div>
            <div className="text-[12.5px] tracking-[0.18em] uppercase text-[#8A7E6E]">
              {courseInfo.durationLabel}
            </div>
          </div>

          <div className="grid md:grid-cols-2 gap-5" data-testid="curso-modules-list">
            {courseModules.map((m) => (
              <article
                key={m.id}
                className="group relative bg-[#FAF4E8] border border-[#DBCEB4] rounded-2xl p-6 lg:p-7 transition-shadow hover:shadow-[0_20px_40px_-25px_rgba(74,46,32,0.35)]"
                data-testid={`curso-module-card-${m.id}`}
              >
                <div className="flex items-start justify-between gap-4 mb-4">
                  <span className="font-serif italic text-[#B8806E] text-[34px] leading-none">
                    {String(m.id).padStart(2, '0')}
                  </span>
                  <span className="inline-flex items-center gap-1.5 text-[11px] tracking-[0.18em] uppercase text-[#8A7E6E]">
                    <Clock size={12} strokeWidth={1.8} /> {m.duration}
                  </span>
                </div>
                <h3 className="font-serif text-[22px] lg:text-[24px] leading-[1.18] text-[#2E2A23] tracking-[-0.005em]">
                  {m.name}
                </h3>
                <p className="mt-3 text-[14.5px] leading-[1.7] text-[#2E2A23]/75 font-light">
                  {m.summary}
                </p>
                <div className="mt-5 flex items-center justify-between">
                  <span className="inline-flex items-center gap-1.5 text-[11.5px] tracking-[0.16em] uppercase text-[#8A7E6E]">
                    <Lock size={12} strokeWidth={1.8} /> Acceso tras inscripción
                  </span>
                  <PlayCircle
                    size={26}
                    strokeWidth={1.4}
                    className="text-[#C49A8C] group-hover:text-[#B8806E] transition-colors"
                  />
                </div>
              </article>
            ))}
          </div>
        </section>

        {/* GUARANTEES */}
        <section className="mt-24 lg:mt-32 grid lg:grid-cols-[0.9fr,1.1fr] gap-12">
          <div>
            <div className="text-[11px] tracking-[0.28em] uppercase text-[#8A7E6E] mb-4">
              Lo que te llevás
            </div>
            <h2 className="font-serif text-[34px] lg:text-[44px] leading-[1.06] tracking-[-0.015em]">
              Mucho más que
              <span className="italic text-[#B8806E]"> 8 videos</span>
            </h2>
            <p className="mt-5 text-[15.5px] leading-[1.8] text-[#2E2A23]/80 font-light max-w-[480px]">
              Pensé este curso como un acompañamiento real. Por eso incluye material
              descargable, atención por WhatsApp y acceso flexible.
            </p>
          </div>
          <ul className="space-y-3.5" data-testid="curso-guarantees">
            {courseInfo.guarantees.map((g, i) => (
              <li
                key={i}
                className="flex items-start gap-4 px-6 py-4 rounded-2xl bg-[#FAF4E8] border border-[#DBCEB4]"
              >
                <CheckCircle2
                  size={20}
                  strokeWidth={1.7}
                  className="text-[#8FA08A] flex-none mt-0.5"
                />
                <span className="text-[15px] leading-[1.7] text-[#2E2A23]/90">{g}</span>
              </li>
            ))}
          </ul>
        </section>

        {/* PRICING / INSCRIPCIÓN */}
        <section
          id="inscripcion"
          className="mt-24 lg:mt-32 relative overflow-hidden rounded-[32px] border border-[#DBCEB4] bg-gradient-to-br from-[#F8E7DD] via-[#F4EDE2] to-[#F2E9D9] p-8 lg:p-14"
        >
          <div className="absolute -top-10 -right-10 w-48 h-48 rounded-full bg-[#E8C9C0]/40 blur-3xl pointer-events-none" />
          <div className="absolute -bottom-12 -left-10 w-56 h-56 rounded-full bg-[#C5D2BB]/40 blur-3xl pointer-events-none" />

          <div className="relative grid lg:grid-cols-[1fr,1fr] gap-12 items-center">
            <div>
              <div className="text-[11px] tracking-[0.28em] uppercase text-[#8A7E6E] mb-4">
                Inscripción
              </div>
              <h2 className="font-serif text-[36px] lg:text-[48px] leading-[1.04] tracking-[-0.015em]">
                Sumate al
                <span className="italic text-[#B8806E]"> curso</span>
              </h2>

              <div className="mt-7 flex items-end gap-5">
                <div>
                  <div className="text-[10px] tracking-[0.3em] uppercase text-[#8A7E6E]">
                    Precio Uruguay
                  </div>
                  <div
                    className="font-serif text-[52px] lg:text-[64px] leading-none text-[#2E2A23] mt-1"
                    data-testid="curso-price-uyu"
                  >
                    $<span className="font-medium">{courseInfo.priceUYU}</span>
                    <span className="text-[18px] tracking-[0.18em] uppercase text-[#8A7E6E] ml-2 align-middle">
                      UYU
                    </span>
                  </div>
                </div>
                <div className="border-l border-[#C5B496] pl-5 pb-3">
                  <div className="text-[10px] tracking-[0.3em] uppercase text-[#8A7E6E]">
                    Internacional
                  </div>
                  <div
                    className="font-serif text-[34px] leading-none text-[#B8806E] mt-1"
                    data-testid="curso-price-usd"
                  >
                    USD <span className="font-medium">{courseInfo.priceUSD}</span>
                  </div>
                </div>
              </div>

              <p className="mt-6 text-[14px] leading-[1.7] text-[#2E2A23]/75 max-w-[460px]">
                Pago único · Sin renovación automática · Acceso inmediato luego de
                confirmar el pago.
              </p>
            </div>

            {/* payment options */}
            <div className="space-y-4" data-testid="curso-payment-options">
              <a
                href={buildWhatsAppLink('USD')}
                target="_blank"
                rel="noreferrer"
                className="block rounded-2xl border border-[#DBCEB4] bg-[#FAF4E8] p-6 hover:bg-white transition-colors"
                data-testid="curso-cta-paypal"
              >
                <div className="flex items-start gap-4">
                  <span className="w-12 h-12 rounded-full bg-[#FFC439] grid place-items-center flex-none">
                    <CreditCard size={20} strokeWidth={1.6} className="text-[#003087]" />
                  </span>
                  <div className="flex-1">
                    <div className="font-serif text-[20px] text-[#2E2A23] leading-tight">
                      PayPal · Tarjeta internacional
                    </div>
                    <p className="text-[13.5px] text-[#2E2A23]/75 mt-1 leading-[1.6]">
                      USD {courseInfo.priceUSD} · Aceptamos tarjetas de cualquier país.
                      No necesitás cuenta de PayPal.
                    </p>
                    <span className="inline-flex items-center gap-1.5 text-[12px] tracking-[0.14em] uppercase text-[#B8806E] mt-3">
                      <MessageCircle size={13} strokeWidth={1.8} />
                      Coordinar pago por WhatsApp
                    </span>
                  </div>
                </div>
              </a>

              <a
                href={buildWhatsAppLink('UYU')}
                target="_blank"
                rel="noreferrer"
                className="block rounded-2xl border border-[#DBCEB4] bg-[#FAF4E8] p-6 hover:bg-white transition-colors"
                data-testid="curso-cta-transfer"
              >
                <div className="flex items-start gap-4">
                  <span className="w-12 h-12 rounded-full bg-[#E8DDC8] grid place-items-center flex-none">
                    <Landmark size={20} strokeWidth={1.6} className="text-[#6B7C66]" />
                  </span>
                  <div className="flex-1">
                    <div className="font-serif text-[20px] text-[#2E2A23] leading-tight">
                      Transferencia bancaria · Uruguay
                    </div>
                    <p className="text-[13.5px] text-[#2E2A23]/75 mt-1 leading-[1.6]">
                      ${courseInfo.priceUYU} UYU · Te paso los datos de cuenta por
                      WhatsApp para más seguridad.
                    </p>
                    <span className="inline-flex items-center gap-1.5 text-[12px] tracking-[0.14em] uppercase text-[#B8806E] mt-3">
                      <MessageCircle size={13} strokeWidth={1.8} />
                      Pedir datos por WhatsApp
                    </span>
                  </div>
                </div>
              </a>

              <div className="flex items-center justify-center gap-4 pt-2 text-[#8A7E6E]">
                <ShieldCheck size={16} strokeWidth={1.6} />
                <span className="text-[11.5px] tracking-[0.18em] uppercase">
                  Datos protegidos · Devolución 7 días
                </span>
              </div>
            </div>
          </div>
        </section>

        {/* FAQ */}
        <section className="mt-24 lg:mt-32">
          <div className="text-[11px] tracking-[0.28em] uppercase text-[#8A7E6E] mb-4">
            Preguntas frecuentes
          </div>
          <h2 className="font-serif text-[34px] lg:text-[44px] leading-[1.06] tracking-[-0.015em] max-w-[700px]">
            Lo que muchas
            <span className="italic text-[#B8806E]"> me preguntan</span> antes de
            inscribirse
          </h2>

          <div
            className="mt-10 max-w-[860px] divide-y divide-[#DBCEB4]/70 border-y border-[#DBCEB4]/70"
            data-testid="curso-faq-list"
          >
            {courseFAQ.map((f, i) => (
              <FAQItem key={i} idx={i} q={f.q} a={f.a} />
            ))}
          </div>
        </section>

        {/* about author trust */}
        <section className="mt-24 lg:mt-32 grid lg:grid-cols-[0.9fr,1.1fr] gap-12 items-center">
          <div className="aspect-[4/5] rounded-[24px] overflow-hidden border-[6px] border-[#FAF4E8] shadow-[0_30px_60px_-30px_rgba(74,46,32,0.4)] max-w-[420px]">
            <img
              src={profile.about}
              alt="Karina Negrin"
              className="w-full h-full object-cover photo-no-watermark"
            />
          </div>
          <div>
            <div className="text-[11px] tracking-[0.28em] uppercase text-[#8A7E6E] mb-4">
              Quién te acompaña
            </div>
            <h2 className="font-serif text-[34px] lg:text-[42px] leading-[1.06] tracking-[-0.015em]">
              Soy Karina <span className="italic text-[#B8806E]">Negrin</span>
            </h2>
            <p className="mt-5 text-[15.5px] leading-[1.85] text-[#2E2A23]/80 font-light">
              {profile.longBio}
            </p>
            <div className="mt-7 flex flex-wrap gap-2.5">
              {['Psicóloga', 'Puericultora', 'Doula', 'Psicología perinatal'].map((t) => (
                <span
                  key={t}
                  className="px-4 py-2 rounded-full bg-[#FAF4E8] border border-[#C5B496] text-[12.5px] text-[#2E2A23]"
                >
                  {t}
                </span>
              ))}
            </div>
          </div>
        </section>

        {/* final CTA */}
        <section className="mt-24 lg:mt-32 text-center max-w-[720px] mx-auto">
          <h2 className="font-serif text-[34px] lg:text-[44px] leading-[1.06] tracking-[-0.015em]">
            ¿Lista para empezar?
          </h2>
          <p className="mt-5 text-[15.5px] leading-[1.8] text-[#2E2A23]/80 font-light">
            Reservá tu lugar y te paso los pasos a seguir. Cualquier duda previa,
            escribime sin compromiso.
          </p>
          <div className="mt-8 flex flex-wrap items-center justify-center gap-4">
            <a
              href="#inscripcion"
              className="btn-primary"
              data-testid="curso-final-cta"
            >
              <Sparkles size={16} strokeWidth={1.6} />
              Inscribirme al curso
            </a>
            <a
              href={profile.whatsapp}
              target="_blank"
              rel="noreferrer"
              className="btn-ghost"
              data-testid="curso-final-cta-question"
            >
              <MessageCircle size={16} strokeWidth={1.6} />
              Tengo una pregunta
            </a>
          </div>
        </section>
      </main>

      <footer className="mt-24 lg:mt-32 border-t border-[#DBCEB4] py-10 bg-[#F4EDE2]">
        <div className="max-w-[1200px] mx-auto px-5 lg:px-8 flex flex-wrap items-center justify-between gap-4 text-[12px] text-[#8A7E6E]">
          <span>© Karina Negrin · {new Date().getFullYear()}</span>
          <Link to="/" className="hover:text-[#B8806E] transition-colors">
            Volver al sitio
          </Link>
        </div>
      </footer>
    </div>
  );
};

export default CursoLanding;
